import React from 'react';
import { DEFAULT_GEO_LOCATION } from 'utils/coordinates';
import { GEO_LOCATION } from 'utils/fieldTypes';
import noop from 'utils/noop';
import FieldDropdown from '../fieldDropdown';
import OrderByLocationPicker from './OrderByLocationPicker';
import {
  StyledCoreDockingBar,
  LimitAndOrderByRowContainer,
  FlexContainer,
  InputContainer,
  StyledNumberInput,
  StyledCoreDropdown,
} from './LimitAndOrderByRow.styled';

// type Props = {
//   /**
//    * Callback function when the order by values changes.
//    *
//    * ```
//    * changeOrderBy(orderBy)
//    * ```
//    * @param { Object } orderBy: The new orderBy object.
//    * @returns { void }
//    */
//   changeOrderBy: () => {},
//   /**
//    * Callback function when size limit changes.
//    *
//    * ```
//    * changeSizeLimit(sizeLimit)
//    * ```
//    * @param { Number } sizeLimit: The new size limit value.
//    * @returns { void }
//    */
//   changeSizeLimit: (sizeLimit: number) => {},
//   /** Additional classes. */
//   className: string,
//   /** Additional content. */
//   content: node,
//   /** The dynamic location value. */
//   dynamicLocation: ?string,
//   /** The selected field to order by. */
//   field: string,
//   /** Array of all the available fields to order by. */
//   fields: [],
//   /** If sorting is ascending. */
//   isSortAsc: boolean,
//   /** The size limit value. */
//   sizeLimit: number,
//   /** The max size limit value. */
//   sizeLimitMax: number,
//   /** The geo location object value. */
//   location: {},
//   /** Object of arrays of user mappings by type. */
//   userMappings: {},
// };

export default class LimitAndOrderByRow extends React.Component {
  static defaultProps = {
    changeOrderBy: noop,
    changeSizeLimit: noop,
    isSortAsc: true,
    field: '',
    sizeLimit: '',
    userMappings: {},
  };

  /**
   * hasLocation
   * Helper function to determine if location exists.
   * @returns { Boolean } If location exists.
   */
  hasLocation = () => {
    const { dynamicLocation, location } = this.props;
    return dynamicLocation !== undefined || (location && location.lat !== undefined);
  };

  /**
   * handleChangeField
   * Callback function when field is changed.
   * @param { Object } e: The change event.
   * @param { Object } updatedProps: The updated props containing the newly select field value and type.
   * @returns { void }
   */
  handleChangeField = (e, { type, value }) => {
    const { changeOrderBy, isSortAsc, dynamicLocation, location } = this.props;
    const fieldIsGeo = type === GEO_LOCATION;
    const hasLocation = this.hasLocation() && fieldIsGeo;
    const newLocation = fieldIsGeo ? DEFAULT_GEO_LOCATION : undefined;

    const orderBy = {
      dynamicLocation: hasLocation ? dynamicLocation : undefined,
      field: value,
      asc: isSortAsc,
      location: hasLocation ? location : newLocation,
    };
    changeOrderBy(orderBy);
  };

  /**
   * handleChangeSizeLimit
   * Callback function when size limit is changed.
   * @param { Object } e: The change event.
   * @param { Object } updatedProps: The updated props containing the updated size limit value.
   * @returns { void }
   */
  handleChangeSizeLimit = (e, { value }) => {
    const { changeSizeLimit, sizeLimitMax } = this.props;
    let returnValue = value;
    if (sizeLimitMax && parseFloat(value, 10) > sizeLimitMax) {
      returnValue = `${sizeLimitMax}`;
    } else if (value === '0') {
      returnValue = '';
    }
    changeSizeLimit(returnValue);
  };

  /**
   * handleChangeLocation
   * Callback function when location values are changed.
   * @param { Object } e: The change event.
   * @param { Object } locationValues: The updated location values.
   * @returns { void }
   */
  handleChangeLocation = (e, locationValues) => {
    const { changeOrderBy, field, isSortAsc } = this.props;
    const orderBy = {
      asc: isSortAsc,
      field,
      ...locationValues,
    };
    changeOrderBy(orderBy);
  };

  /**
   * handleChangeSort
   * Callback function sort value if changed.
   * @param { Object } e: The change event.
   * @param { Object } updatedProps: The updated props with new sort value.
   * @returns { void }
   */
  handleChangeSort = (e, { value }) => {
    const { changeOrderBy, field, dynamicLocation, location } = this.props;
    const orderBy = {
      asc: value,
      dynamicLocation,
      field,
      location,
    };
    changeOrderBy(orderBy);
  };

  /**
   * getSortOptions
   * Get the dropdown options for the sort by dropdown.
   * @returns { Array } The dropdown options.
   */
  getSortOptions = () => [
    {
      text: 'Sort Ascending',
      icon: 'sort amount up',
      value: true,
    },
    {
      text: 'Sort Descending',
      icon: 'sort amount down',
      value: false,
    },
  ];

  getSizePlaceholder = () => {
    const { sizeLimitMax } = this.props;
    return sizeLimitMax ? `Up to ${sizeLimitMax}` : '# of items';
  };

  render() {
    const {
      content,
      dynamicLocation,
      field,
      fields,
      isSortAsc,
      location,
      sizeLimit,
      userMappings,
    } = this.props;
    const hasLocationField = field && this.hasLocation();

    return (
      <StyledCoreDockingBar deferTime={200} dockOnBottom dockedClassName="cssLimitAndOrderDocked">
        <LimitAndOrderByRowContainer>
          <FlexContainer>
            <label>Items To Return</label>
            <InputContainer>
              <StyledNumberInput
                allowNegative={false}
                onChange={this.handleChangeSizeLimit}
                placeholder={this.getSizePlaceholder()}
                value={sizeLimit}
              />
            </InputContainer>
          </FlexContainer>
          <FlexContainer maxWidth={440}>
            <label>Ordered By</label>
            <InputContainer>
              <FieldDropdown
                clearable
                className="cssOrderByInputFieldDropdownInput"
                fields={fields}
                hideReadOnlyLabel
                onChange={this.handleChangeField}
                upward
                value={field}
              />
              <StyledCoreDropdown
                disabled={!field}
                floating
                hideTextOnSelection
                onChange={this.handleChangeSort}
                options={this.getSortOptions()}
                upward
                value={isSortAsc}
              />
            </InputContainer>
          </FlexContainer>
          {hasLocationField && (
            <FlexContainer>
              <label>From Location</label>
              <InputContainer>
                <OrderByLocationPicker
                  dynamicLocation={dynamicLocation}
                  location={location}
                  onChange={this.handleChangeLocation}
                  userMappings={userMappings}
                />
              </InputContainer>
            </FlexContainer>
          )}
          {content}
        </LimitAndOrderByRowContainer>
      </StyledCoreDockingBar>
    );
  }
}
